"use client";

import { useEffect, useRef } from "react";

const THREAD_COLOR = "#c8a97e";
const POINT_COUNT = 333;
const LEG_COUNT = 9;
const SPIDER_COUNT = 1;
const MAX_GRAB = 8;
const SIZE = 0.6;

type Point = { x: number; y: number; len: number; r: number };
type Vec = { x: number; y: number };

type Spider = {
  follow: (x: number, y: number) => void;
  tick: (t: number) => void;
  resize: (prevW: number, prevH: number) => void;
};

export default function SpiderCursor() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      window.matchMedia("(pointer: coarse)").matches
    ) {
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const { sin, cos, PI, hypot, min, max } = Math;

    const many = <T,>(n: number, f: (i: number) => T): T[] =>
      Array.from({ length: n }, (_, i) => f(i));
    const rnd = (x = 1, dx = 0) => Math.random() * x + dx;
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const vec = (x: number, y: number): Vec => ({ x, y });

    function drawCircle(x: number, y: number, r: number) {
      ctx!.beginPath();
      ctx!.ellipse(x, y, r, r, 0, 0, PI * 2);
      ctx!.fill();
    }

    function noise(x: number, y: number, t = 101) {
      const w0 = sin(
        0.3 * x + 1.4 * t + 2.0 + 2.5 * sin(0.4 * y + -1.3 * t + 1.0)
      );
      const w1 = sin(
        0.2 * y + 1.5 * t + 2.8 + 2.3 * sin(0.5 * x + -1.2 * t + 0.5)
      );
      return w0 + w1;
    }

    function drawLine(x0: number, y0: number, x1: number, y1: number) {
      ctx!.beginPath();
      ctx!.moveTo(x0, y0);
      many(100, (i) => {
        const p = (i + 1) / 100;
        const x = lerp(x0, x1, p);
        const y = lerp(y0, y1, p);
        const k =
          SIZE *
          2 *
          noise((x / 5 + x0) / SIZE, (y / 5 + y0) / SIZE);
        ctx!.lineTo(x + k, y + k);
      });
      ctx!.stroke();
    }

    function spawn(): Spider {
      const pts: Point[] = many(POINT_COUNT, () => ({
        x: rnd(window.innerWidth),
        y: rnd(window.innerHeight),
        len: 0,
        r: 0,
      }));
      const legs: Vec[] = many(LEG_COUNT, (i) => ({
        x: cos((i / LEG_COUNT) * PI * 2),
        y: sin((i / LEG_COUNT) * PI * 2),
      }));

      const seed = rnd(100);
      let tx = rnd(window.innerWidth);
      let ty = rnd(window.innerHeight);
      let x = rnd(window.innerWidth);
      let y = rnd(window.innerHeight);
      const kx = rnd(0.8, 0.8);
      const ky = rnd(0.8, 0.8);
      const walkRadius = vec(rnd(50, 50) * SIZE, rnd(50, 50) * SIZE);
      let legScale = (window.innerWidth / rnd(100, 150)) * SIZE;

      function paintPt(pt: Point) {
        if (pt.len) {
          legs.forEach((leg) => {
            const hx = x + leg.x * legScale;
            const hy = y + leg.y * legScale;
            drawLine(
              lerp(hx, pt.x, pt.len * pt.len),
              lerp(hy, pt.y, pt.len * pt.len),
              hx,
              hy
            );
          });
        }
        drawCircle(pt.x, pt.y, pt.r);
      }

      return {
        follow(px, py) {
          tx = px;
          ty = py;
        },

        tick(t) {
          const fx = tx + cos(t * kx + seed) * walkRadius.x;
          const fy = ty + sin(t * ky + seed) * walkRadius.y;

          x += min(window.innerWidth / 100, (fx - x) / 10);
          y += min(window.innerWidth / 100, (fy - y) / 10);

          let grabbed = 0;
          const grabReach = (window.innerWidth / 10) * SIZE;
          pts.forEach((pt) => {
            const dx = pt.x - x;
            const dy = pt.y - y;
            const dist = hypot(dx, dy);
            let r = min(2, window.innerWidth / dist / 5);
            const increasing = dist < grabReach && grabbed++ < MAX_GRAB;
            if (increasing) r *= 1.5;
            pt.r = r;
            pt.len = max(0, min(pt.len + (increasing ? 0.1 : -0.1), 1));
            paintPt(pt);
          });
        },

        resize(prevW, prevH) {
          const sx = window.innerWidth / prevW;
          const sy = window.innerHeight / prevH;
          pts.forEach((pt) => {
            pt.x *= sx;
            pt.y *= sy;
          });
          x = min(x * sx, window.innerWidth);
          y = min(y * sy, window.innerHeight);
          tx = min(tx * sx, window.innerWidth);
          ty = min(ty * sy, window.innerHeight);
          legScale = (window.innerWidth / rnd(100, 150)) * SIZE;
        },
      };
    }

    const spiders = many(SPIDER_COUNT, spawn);

    const handlePointerMove = (e: PointerEvent) => {
      spiders.forEach((spider) => spider.follow(e.clientX, e.clientY));
    };
    window.addEventListener("pointermove", handlePointerMove);

    let w = 0;
    let h = 0;
    let rafId = 0;

    const resize = () => {
      const prevW = w || window.innerWidth;
      const prevH = h || window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (prevW !== w || prevH !== h) {
        spiders.forEach((spider) => spider.resize(prevW, prevH));
      }
    };
    resize();

    const anim = (t: number) => {
      if (w !== window.innerWidth || h !== window.innerHeight) resize();
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = ctx.strokeStyle = THREAD_COLOR;
      spiders.forEach((spider) => spider.tick(t / 1000));
      rafId = requestAnimationFrame(anim);
    };
    rafId = requestAnimationFrame(anim);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="fixed inset-0 z-[9998] pointer-events-none"
    />
  );
}
